import { getUserAuthInfo, getAccountInfo } from "@/04-drivers/dal/user";
import { useQuery, QueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { z } from "zod";
import { COUPON_STATUS, CouponProps } from "./tiktok/account/const";
import { useRef } from "react";

// 创建 QueryClient 实例
export const queryClient = new QueryClient();

export type Role = z.infer<typeof UserAuthInfoSchema>["role_list"][number];

const paidUserIdList = [1, 2, 3, 6, 7, 8, 9, 10, 1001, 1002, 1003, 1004, 1005];

const highLevelUserIdList = [1, 2, 6, 7, 8, 1001, 1002, 1003];

// id > 100 是企业定制版

function getIsPaidUser(roleId: number) {
  return paidUserIdList.includes(roleId) || (roleId > 100 && roleId < 1000);
}

function getIsHighLevelUser(roleId: number) {
  return (
    highLevelUserIdList.includes(roleId) || (roleId > 100 && roleId < 1000)
  );
}

function getIsTheMostHighLevelUser(roleId: number) {
  return (
    roleId === 1 ||
    roleId === 6 ||
    (roleId > 100 && roleId < 1000) ||
    roleId === 1001
  );
}

const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  phone: z.string().nullish(),
  username: z.string(),
  image: z.string().nullish(),
  identityInfo: z.string().nullish(),
  shareCode: z.string().nullish(),
  gray_feature: z.array(z.string()),
});

const UserAuthInfoSchema = z.object({
  role_list: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      desc: z.string(),
      version: z.number(),
      start_at: z.number(),
      last_time: z.number(),
      ctime: z.number(),
      active_time: z.number(),
    }),
  ),
  account_id: z.number(),
  is_owner: z.boolean(),
  is_admin: z.boolean(),
});

const AccountInfoSchema = z.object({
  account_id: z.number(),
  sub_account_count: z.number(),
  create_time: z.number(),
  update_time: z.number(),
  owner_user_id: z.string(),
  user_info_list: z.array(
    z.object({
      user_id: z.string(),
      email: z.string(),
      phone_number: z.string(),
      comment: z.string().nullish(),
      ctime: z.number(),
      active_time: z.number(),
      is_owner: z.boolean(),
      is_admin: z.boolean(),
      role_id: z.number(),
      role_name: z.string(),
      role_desc: z.string(),
      role_version: z.number(),
    }),
  ),
  package_id: z.number(),
  name: z.string(),
  desc: z.string(),
  is_never_paid: z.boolean(),
  is_referred: z.boolean(),
});

export const getIsClientDev = () => {
  if (typeof window === "undefined") {
    return false;
  }
  const host = window.location.host;
  return (
    host.includes("127.0.0.1") ||
    host.includes("localhost") ||
    host.includes("chj") ||
    host.includes("pre")
  );
};

export const useUserInfo = () => {
  const isDev = getIsClientDev();
  const authInfoFirstInit = useRef(true);
  const accountInfoFirstInit = useRef(true);
  const {
    data: user,
    isLoading: isUserLoading,
    refetch,
  } = useQuery({
    queryKey: ["/api/user/luciaUserInfo"],
    retry: 0,
    staleTime: 1000 * 60 * 60,
    queryFn: async ({ queryKey }) => {
      try {
        // 优先从 window 获取数据
        if (
          typeof window !== "undefined" &&
          (window as any).__INITIAL_USER_DATA__
        ) {
          return (window as any).__INITIAL_USER_DATA__ as z.infer<
            typeof UserSchema
          >;
        }
        const res = await fetch(queryKey[0]);
        const data = await res.json();
        return UserSchema.parse(data?.user);
      } catch (error) {
        return null;
      }
    },
  });

  const { data: userAuthInfo, isLoading: isUserAuthInfoLoading } = useQuery({
    enabled: !!user?.id,
    retry: 0,
    staleTime: 1000 * 60 * 60,
    refetchInterval: 1000 * 60 * 60,
    refetchIntervalInBackground: true,
    queryKey: ["/api/v2/permission/info" + user?.id],
    queryFn: async () => {
      try {
        // 优先从 window 获取数据
        if (
          typeof window !== "undefined" &&
          (window as any).__INITIAL_USER_AUTH_INFO__ &&
          authInfoFirstInit.current
        ) {
          authInfoFirstInit.current = false;
          return (window as any).__INITIAL_USER_AUTH_INFO__ as z.infer<
            typeof UserAuthInfoSchema
          >;
        }
        const res = await getUserAuthInfo(
          user!.id,
          "user.personal.permission.info",
        );
        return UserAuthInfoSchema.parse(res);
      } catch (error) {
        return null;
      }
    },
  });

  const { data: accountInfo, isLoading: isAccountInfoLoading } = useQuery({
    enabled: !!userAuthInfo?.account_id,
    retry: 0,
    staleTime: 1000 * 60 * 60,
    refetchInterval: 1000 * 60 * 60,
    refetchIntervalInBackground: true,
    queryKey: ["/api/v2/account/info" + userAuthInfo?.account_id],
    queryFn: async () => {
      try {
        // 优先从 window 获取数据
        if (
          typeof window !== "undefined" &&
          (window as any).__INITIAL_ACCOUNT_INFO__ &&
          accountInfoFirstInit.current
        ) {
          accountInfoFirstInit.current = false;
          return (window as any).__INITIAL_ACCOUNT_INFO__ as z.infer<
            typeof AccountInfoSchema
          >;
        }
        const res = await getAccountInfo(
          userAuthInfo!.account_id,
          "user.personal.account.info",
        );
        return AccountInfoSchema.parse(res?.account_info);
      } catch (error) {
        return null;
      }
    },
  });

  const { data: seaBayCount, refetch: refetchSeaBayCount } = useQuery({
    enabled: !!userAuthInfo?.account_id,
    queryKey: ["/api/v3/seaBay/info", userAuthInfo?.account_id],
    queryFn: async () => {
      const res = await fetch("/api/v3/account/virtual_coin");
      const data = await res.json();
      return (data?.balance as number) ?? 0;
    },
  });

  const { data: userCouponList } = useQuery({
    enabled: !!user?.id,
    queryKey: ["/api/v3/account/discount_coupon"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/v3/account/discount_coupon");
        const data = await res.json();
        return (data?.discount_coupon_list as CouponProps[]) || [];
      } catch (error) {
        return [];
      }
    },
  });

  const isLoading =
    isUserLoading || isUserAuthInfoLoading || isAccountInfoLoading;
  const isNotPaidUser = !getIsPaidUser(userAuthInfo?.role_list?.[0]?.id ?? 4);
  const isHighLevelUser = getIsHighLevelUser(
    userAuthInfo?.role_list?.[0]?.id ?? 4,
  );
  const isTheMostHighLevelUser = getIsTheMostHighLevelUser(
    userAuthInfo?.role_list?.[0]?.id ?? 4,
  );
  const isNeverPaid = accountInfo?.is_never_paid ?? true;
  // 有邀请码但未获取会员
  const isHadShareCodeAndNotGetMember =
    !!user?.shareCode && !accountInfo?.is_referred;
  function getIsGray(feature: string) {
    return isDev || (user?.gray_feature ?? []).includes(feature);
  }
  return {
    user,
    isLogin: !!user,
    userAuthInfo,
    accountInfo,
    isUserLoading,
    isUserAuthInfoLoading,
    isAccountInfoLoading,
    isLoading,
    refetch,
    isNotPaidUser,
    isNeverPaid,
    isReferred: accountInfo?.is_referred,
    seaBayCount,
    refetchSeaBayCount,
    isHighLevelUser,
    isOwner: !!userAuthInfo?.is_owner,
    userCouponList:
      userCouponList?.filter((c) => c.status === COUPON_STATUS.UNUSED) || [],
    getIsGray,
    isTheMostHighLevelUser,
    isHadShareCodeAndNotGetMember,
  };
};
